import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useBulletin, useChangerStatutBulletin, useSupprimerBulletin } from '../hooks/useBulletins';
import { useConfirm } from '../../../shared/components/ConfirmDialog/useConfirm';
import { bulletinsApi } from '../api/bulletins.api';
import { configurationApi } from '../../configuration/api/configuration.api';
import { LigneCalcul } from '../types';

const MOIS_NOMS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const CATEGORIES_ORDRE = ['SALAIRE_BASE', 'PRIME', 'INDEMNITE', 'RETENUE', 'COTISATION', 'TAXE'];

const CATEGORIES_LABELS: Record<string, string> = {
  SALAIRE_BASE: 'Salaire de base',
  PRIME: 'Primes',
  INDEMNITE: 'Indemnités',
  RETENUE: 'Retenues',
  COTISATION: 'Cotisations sociales',
  TAXE: 'Taxes & impôts',
};

const f = (n: number) => n.toLocaleString('fr-FR') + ' F';

export function BulletinDetailPage() {
  const { idBulletin } = useParams<{ idBulletin: string }>();
  const navigate = useNavigate();
  const [telechargement, setTelechargement] = useState(false);

  const { data: bulletin, isLoading, isError } = useBulletin(idBulletin);
  const changerStatut = useChangerStatutBulletin();
  const supprimerBulletin = useSupprimerBulletin();
  const confirm = useConfirm();
  const QR_CODE_URL_PAR_DEFAUT = 'https://www.caritasbenin.org/contacternous/';
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);

  // Charger l'URL QR code depuis les paramètres si bulletin PAYE
  React.useEffect(() => {
    if (bulletin?.statut === 'PAYE') {
      setQrCodeUrl(QR_CODE_URL_PAR_DEFAUT); // afficher immédiatement avec la valeur par défaut
      configurationApi.listerParametres()
        .then((params: any[]) => {
          const p = params?.find((x: any) => x.code === 'QR_CODE_URL');
          if (p?.valeur) setQrCodeUrl(p.valeur); // remplacer si un paramètre est configuré
        })
        .catch(() => {}); // garder la valeur par défaut en cas d'erreur
    }
  }, [bulletin?.statut]);
  const [erreurDocx, setErreurDocx] = useState('');
  const [erreurStatut, setErreurStatut] = useState('');
  const [erreurSuppression, setErreurSuppression] = useState('');

  const handleTelecharger = async () => {
    if (!idBulletin || !bulletin) return;
    setTelechargement(true);
    setErreurDocx('');
    try {
      const blob = await bulletinsApi.telecharger(idBulletin);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin-${bulletin.nomEmploye}-${bulletin.mois}-${bulletin.annee}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setErreurDocx(err?.response?.data?.message || 'Erreur lors du téléchargement du fichier DOCX');
    } finally {
      setTelechargement(false);
    }
  };

  const handleSupprimer = async () => {
    if (!idBulletin || !bulletin) return;
    const ok = await confirm({
      title: 'Supprimer ce bulletin ?',
      message: `Le bulletin de ${bulletin.prenomEmploye} ${bulletin.nomEmploye} (${MOIS_NOMS[bulletin.mois - 1]} ${bulletin.annee}) sera définitivement supprimé.`,
      variant: 'danger',
      labelConfirm: 'Supprimer',
    });
    if (!ok) return;
    setErreurSuppression('');
    try {
      await supprimerBulletin.mutateAsync(idBulletin);
      navigate('/paie/bulletins');
    } catch (err: any) {
      setErreurSuppression(err?.response?.data?.message || 'Impossible de supprimer ce bulletin');
    }
  };

  const handleChangerStatut = async (statut: 'VALIDE' | 'PAYE') => {
    if (!idBulletin) return;
    setErreurStatut('');
    try {
      await changerStatut.mutateAsync({ idBulletin, statut });
    } catch (err: any) {
      setErreurStatut(err?.response?.data?.message || `Impossible de passer au statut ${statut}`);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="animate-pulse space-y-5">
          <div className="h-5 bg-slate-200 rounded w-32" />
          <div className="h-9 bg-slate-200 rounded w-64" />
          <div className="h-5 bg-slate-200 rounded w-48" />
          <div className="grid grid-cols-3 gap-6 mt-6">
            <div className="col-span-2 h-72 bg-slate-200 rounded-2xl" />
            <div className="h-72 bg-slate-200 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !bulletin) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="bg-danger-light border border-danger rounded-2xl p-8 text-center">
          <p className="text-danger font-semibold text-lg">Bulletin introuvable</p>
          <p className="text-danger text-sm mt-2">
            Ce bulletin n'existe pas ou vous n'y avez pas accès.
          </p>
          <button
            onClick={() => navigate('/paie/bulletins')}
            className="mt-5 px-5 py-2.5 bg-danger text-content-inverted rounded-xl text-sm font-medium hover:bg-red-700 transition-colors"
          >
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  // Grouper les lignes par catégorie
  const lignesParCategorie = CATEGORIES_ORDRE.reduce((acc, cat) => {
    const lignes = (bulletin.lignesCalcul || [])
      .filter((l: LigneCalcul) => l.typeCalcul === cat)
      .sort((a: LigneCalcul, b: LigneCalcul) => a.ordre - b.ordre);
    if (lignes.length > 0) acc[cat] = lignes;
    return acc;
  }, {} as Record<string, LigneCalcul[]>);

  // Lignes de catégories non reconnues
  const autresLignes = (bulletin.lignesCalcul || []).filter(
    (l: LigneCalcul) => !CATEGORIES_ORDRE.includes(l.typeCalcul)
  );

  const isRetenue = (l: LigneCalcul) =>
    ['RETENUE', 'COTISATION', 'TAXE'].includes(l.typeCalcul) || l.montant < 0;

  const moisNom = MOIS_NOMS[bulletin.mois - 1] ?? String(bulletin.mois);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* En-tête */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/paie/bulletins')}
            className="flex items-center gap-1 text-slate-400 hover:text-slate-600 text-sm mb-3 transition-colors"
          >
            ← Retour à la liste
          </button>
          <h1 className="text-3xl font-bold text-slate-900">
            {bulletin.prenomEmploye} {bulletin.nomEmploye}
          </h1>
          <p className="text-slate-500 text-lg mt-1">
            Bulletin de paie — {moisNom} {bulletin.annee}
          </p>
          <div className="flex items-center gap-3 mt-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${
                bulletin.statut === 'VALIDE'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}
            >
              {bulletin.statut}
            </span>
            <span className="text-xs text-slate-400">
              Généré le{' '}
              {new Date(bulletin.dateGeneration).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2 flex-shrink-0 items-end">
          <div className="flex gap-2">
            {bulletin.statut === 'BROUILLON' || bulletin.statut === 'GENERE' ? (
              <button
                onClick={() => handleChangerStatut('VALIDE')}
                disabled={changerStatut.isPending}
                className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-emerald-700 transition-colors disabled:opacity-60"
              >
                ✓ Valider
              </button>
            ) : null}
            {bulletin.statut === 'VALIDE' ? (
              <button
                onClick={() => handleChangerStatut('PAYE')}
                disabled={changerStatut.isPending}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                💳 Marquer payé
              </button>
            ) : null}
            <button
              onClick={handleTelecharger}
              disabled={telechargement}
              className="flex items-center gap-2 bg-primary text-content-inverted px-5 py-2.5 rounded-xl font-medium text-sm hover:bg-primary-hover active:bg-primary-hover transition-colors disabled:opacity-60 shadow-sm shadow-indigo-200"
            >
              {telechargement ? '⏳ Téléchargement…' : '⬇ DOCX'}
            </button>
          </div>
          {bulletin.statut !== 'PAYE' && (
            <button
              onClick={handleSupprimer}
              disabled={supprimerBulletin.isPending}
              className="flex items-center gap-2 bg-danger text-content-inverted px-4 py-2.5 rounded-xl font-medium text-sm hover:bg-red-700 transition-colors disabled:opacity-60"
            >
              🗑 Supprimer
            </button>
          )}
          {erreurDocx && <p className="text-xs text-danger">{erreurDocx}</p>}
          {erreurStatut && <p className="text-xs text-danger">{erreurStatut}</p>}
          {erreurSuppression && <p className="text-xs text-danger">{erreurSuppression}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale : détail du calcul */}
        <div className="lg:col-span-2">
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Détail du calcul
              </h2>
            </div>

            <div className="divide-y divide-slate-100">
              {CATEGORIES_ORDRE.filter(cat => lignesParCategorie[cat]).map(cat => (
                <div key={cat}>
                  <div className="px-6 py-2 bg-slate-50/60">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                      {CATEGORIES_LABELS[cat]}
                    </span>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="px-6 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider w-1/2">
                          Libellé
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Base
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Taux
                        </th>
                        <th className="px-6 py-2 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Montant
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lignesParCategorie[cat].map(ligne => (
                        <tr key={ligne.idLigne} className="hover:bg-slate-50/40 transition-colors">
                          <td className="px-6 py-3 text-sm text-slate-700">{ligne.libelle}</td>
                          <td className="px-4 py-3 text-sm text-slate-400 text-right font-mono tabular-nums">
                            {ligne.baseCalcul > 0 ? f(ligne.baseCalcul) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-400 text-right font-mono tabular-nums">
                            {ligne.taux > 0 ? `${(ligne.taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 4 })} %` : '—'}
                          </td>
                          <td
                            className={`px-6 py-3 text-sm text-right font-mono tabular-nums font-semibold ${
                              isRetenue(ligne) ? 'text-rose-500' : 'text-slate-800'
                            }`}
                          >
                            {isRetenue(ligne) && ligne.montant > 0 ? '−' : ''}
                            {f(Math.abs(ligne.montant))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}

              {autresLignes.length > 0 && (
                <div>
                  <div className="px-6 py-2 bg-slate-50/60">
                    <span className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                      Autres
                    </span>
                  </div>
                  <table className="w-full">
                    <tbody>
                      {autresLignes.map((ligne: LigneCalcul) => (
                        <tr key={ligne.idLigne} className="hover:bg-slate-50/40 transition-colors">
                          <td className="px-6 py-3 text-sm text-slate-700 w-1/2">{ligne.libelle}</td>
                          <td className="px-4 py-3 text-sm text-slate-400 text-right font-mono tabular-nums">
                            {ligne.baseCalcul > 0 ? f(ligne.baseCalcul) : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-400 text-right font-mono tabular-nums">
                            {ligne.taux > 0 ? `${(ligne.taux * 100).toLocaleString('fr-FR', { maximumFractionDigits: 4 })} %` : '—'}
                          </td>
                          <td
                            className={`px-6 py-3 text-sm text-right font-mono tabular-nums font-semibold ${
                              ligne.montant < 0 ? 'text-rose-500' : 'text-slate-800'
                            }`}
                          >
                            {f(ligne.montant)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {(bulletin.lignesCalcul || []).length === 0 && (
                <div className="px-6 py-10 text-center text-slate-400 text-sm">
                  Aucune ligne de calcul disponible.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Colonne latérale : récapitulatif financier */}
        <div className="space-y-4">
          <div className="bg-surface border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest">
                Récapitulatif financier
              </h2>
            </div>

            <div className="p-5 space-y-3">
              {/* Salaire brut */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Salaire brut</span>
                <span className="text-sm font-mono tabular-nums font-semibold text-slate-800">
                  {f(bulletin.salaireBrut)}
                </span>
              </div>

              {/* Déductions */}
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">CNSS salariale</span>
                <span className="text-sm font-mono tabular-nums text-rose-500">
                  − {f(bulletin.montantCNSS)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">ITS</span>
                <span className="text-sm font-mono tabular-nums text-rose-500">
                  − {f(bulletin.montantITS)}
                </span>
              </div>
              {/* Salaire net */}
              <div className="border-t border-slate-100 pt-3 flex justify-between items-center">
                <span className="text-sm font-medium text-slate-700">Salaire net</span>
                <span className="text-sm font-mono tabular-nums font-bold text-slate-800">
                  {f(bulletin.salaireNet)}
                </span>
              </div>

              {/* Déductions complémentaires */}
              {bulletin.avanceAcompte > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Avance / Acompte</span>
                  <span className="text-sm font-mono tabular-nums text-rose-500">
                    − {f(bulletin.avanceAcompte)}
                  </span>
                </div>
              )}
              {bulletin.oppositions > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Oppositions</span>
                  <span className="text-sm font-mono tabular-nums text-rose-500">
                    − {f(bulletin.oppositions)}
                  </span>
                </div>
              )}
              {bulletin.taxesTemporaires > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Taxes temporaires</span>
                  <span className="text-sm font-mono tabular-nums text-rose-500">
                    − {f(bulletin.taxesTemporaires)}
                  </span>
                </div>
              )}
            </div>

            {/* NET À PAYER — pièce maîtresse */}
            <div className="mx-4 mb-4 bg-primary rounded-xl p-5 text-content-inverted shadow-sm shadow-indigo-200">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-muted mb-1">
                Net à payer
              </p>
              <p className="text-4xl font-bold font-mono tabular-nums tracking-tight">
                {f(bulletin.netAPayer)}
              </p>
            </div>
          </div>

          {/* QR Code — visible uniquement pour les bulletins PAYE */}
          {bulletin.statut === 'PAYE' && qrCodeUrl && (
            <div className="bg-surface border border-slate-200 rounded-2xl p-5 text-center space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
                Vérification
              </p>
              <div className="flex justify-center">
                <QRCodeSVG value={qrCodeUrl} size={120} level="M" />
              </div>
              <p className="text-xs text-slate-400">Scanner pour vérifier l'authenticité</p>
            </div>
          )}

          {/* Charges patronales */}
          {(bulletin.montantCNSSPatronal > 0 || bulletin.montantVPS > 0) && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">
                Charges patronales
              </h3>
              {bulletin.montantCNSSPatronal > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">CNSS patronale</span>
                  <span className="text-sm font-mono tabular-nums font-semibold text-slate-700">
                    {f(bulletin.montantCNSSPatronal)}
                  </span>
                </div>
              )}
              {bulletin.montantVPS > 0 && (
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-100">
                  <span className="text-sm text-slate-600">VPS</span>
                  <span className="text-sm font-mono tabular-nums font-semibold text-slate-700">
                    {f(bulletin.montantVPS)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
